import React, { useState, useMemo, useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import { useNavigate } from "react-router";
import { HStack, Box } from "../../components/utils";
import SearchInput from "../../components/shared/SearchInput";
import { IconCTAButton } from "../quickRound/CTAButtonComponents";
import { ShareholderNameRender } from "./ShareHolderAgGridCapTableQuickRoundComponent";
import { useShareholderInstrumentStore } from "../../store/shareholderInstrumentStore";
import {
  ConversionDateRender,
  NumberOfSharesRender,
  SecurityTypeRender,
} from "./ConversionAGComponents";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import GenericTableHeader from "../../shared/TableHeader";
import { handleEventForTracking } from "../../amplitudeAnalytics";

export interface ConversionDto {
  investorId: string;
  investorName?: {
    name: string;
    type: string | null;
  };
  sourceInstrumentClassId: string;
  sourceNoOfShares: number;
  conversionSecurityId?: string;
  noOfSharesAfterConversion?: number;
  date: string;
  conversionDate?: string;
  conversionPPS?: number;
  investmentRecordId?: string;
  tooltipShareHolderName?: string;
  tooltipSourceSecurity?: string;
  tooltipSourceShares?: string;
  tooltipFinalSecurity?: string;
  tooltipConvertedShares?: string;
}

interface ConversionTableGridProps {
  previewData: ConversionDto[];
  onDataChange?: (data: ConversionDto[]) => void;
  isEditable?: boolean;
  isShareholder?: boolean;
}

const ConversionTableGrid: React.FC<ConversionTableGridProps> = ({
  previewData,
  onDataChange,
  isEditable = true,
  isShareholder,
}) => {
  const gridApi = useRef<any>(null);
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const currencyType = getCurrencyType();
  const currencySymbol = getCurrencySymbol();

  const instrumentStore = useShareholderInstrumentStore();
  const shareholders = instrumentStore.shareholders || [];
  const instrumentClasses = instrumentStore.instrumentClasses || [];

  const shareholderLookupMap = useMemo(
    () =>
      new Map(
        shareholders.map((shareholder) => [
          shareholder.id,
          { name: shareholder.name, type: shareholder.type },
        ])
      ),
    [shareholders]
  );

  const instrumentClassLookupMap = useMemo(
    () =>
      new Map(
        instrumentClasses.map((instrument) => [
          instrument.id,
          {
            name: instrument.name,
            type: instrument.subType,
            conversionRatio: instrument.conversionRatio,
            parValue: instrument.parValue,
          },
        ])
      ),
    [instrumentClasses]
  );

  const transformedRowData = useMemo(
    () =>
      previewData.map((row) => {
        const shareholderDetails = shareholderLookupMap.get(row.investorId);
        const sourceInstrument = instrumentClassLookupMap.get(
          row.sourceInstrumentClassId
        );
        const conversionInstrument = row.conversionSecurityId
          ? instrumentClassLookupMap.get(row.conversionSecurityId)
          : null;

        return {
          ...row,
          investorName: {
            name: shareholderDetails?.name || row.investorId,
            type: shareholderDetails?.type || null,
          },
          tooltipShareHolderName: shareholderDetails?.name,
          sourceInstrumentName:
            sourceInstrument?.name || row.sourceInstrumentClassId,
          sourceInstrumentSubType: sourceInstrument?.type || null,
          conversionSecurityName:
            conversionInstrument?.name || row.conversionSecurityId || "",
          conversionSecuritySubType: conversionInstrument?.type || null,
          tooltipSourceSecurity:
            sourceInstrument?.name || row.sourceInstrumentClassId,
          tooltipSourceShares: row.sourceNoOfShares?.toLocaleString(currencyType) || "0",
          tooltipFinalSecurity:
            conversionInstrument?.name || row.conversionSecurityId || "",
          tooltipConvertedShares:
            row.noOfSharesAfterConversion?.toLocaleString(currencyType) || "0",
        };
      }),
    [previewData, shareholderLookupMap, instrumentClassLookupMap]
  );

  const [rowData, setRowData] = useState<ConversionDto[]>(transformedRowData);

  React.useEffect(() => {
    const updatedRowData = transformedRowData.map((row) => ({
      ...row,
      currencyType,
      currencySymbol,
    }));
    setRowData(updatedRowData);
  }, [transformedRowData, currencyType, currencySymbol]);

  const components = useMemo(
    () => ({
      shareholderName: ShareholderNameRender,
      securityTypeRender: SecurityTypeRender,
      numberOfSharesRender: NumberOfSharesRender,
      dateRender: ConversionDateRender,
    }),
    []
  );

  const navigate = useNavigate();

  const onClickShareholderName = (id: string) => {
    if (!isShareholder) {
      handleEventForTracking({ eventName: `Investor Name` });
      navigate(`/shareholders/individual/${id}`);
    }
  };
  // Sidebar configuration
  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
      ],
      defaultToolPanel: "",
    }),
    []
  );

  const columnDefs = useMemo<ColDef<ConversionDto>[]>(
    () => [
      {
        headerName: "Shareholder Name",
        field: "investorName",
        cellRenderer: "shareholderName",
        menuTabs: ["filterMenuTab"],
        initialWidth: 300,
        editable: false,
        pinned: "left",
        valueGetter: (params) => params.data?.investorName,
        filterValueGetter: (params) => params.data?.investorName?.name || "",
        getQuickFilterText: (params) => params.data?.investorName?.name || "",
        valueSetter: (params) => {
          if (params.data && params.newValue) {
            const shareholderDetails = shareholderLookupMap.get(
              params.newValue
            );
            params.data.investorName = {
              name: shareholderDetails?.name || params.newValue,
              type: shareholderDetails?.type || null,
            };
            params.data.investorId = params.newValue;
            return true;
          }
          return false;
        },
        tooltipField: "tooltipShareHolderName",
      },
      {
        headerName: "Source Security Name",
        field: "sourceInstrumentClassId",
        menuTabs: ["filterMenuTab"],
        cellRenderer: "securityTypeRender",
        cellRendererParams: {
          instrumentClassLookupMap,
        },
        editable: isEditable,
        valueGetter: (params) => {
          const instrument = instrumentClassLookupMap.get(
            params.data?.sourceInstrumentClassId || ""
          );
          return instrument?.name || params.data?.sourceInstrumentClassId || "";
        },
        getQuickFilterText: (params) => {
          const instrument = instrumentClassLookupMap.get(
            params.data?.sourceInstrumentClassId || ""
          );
          return instrument?.name || params.data?.sourceInstrumentClassId || "";
        },
        tooltipField: "tooltipSourceSecurity",
      },
      {
        headerName: "Number of shares to be converted",
        field: "sourceNoOfShares",
        cellRenderer: "numberOfSharesRender",
        menuTabs: ["filterMenuTab"],
        cellRendererParams: {
          instrumentClassLookupMap,
        },
        editable: isEditable,
        valueFormatter: (params) =>
          params.value ? params.value.toLocaleString() : "0",
        tooltipField: "tooltipSourceShares",
      },
      {
        headerName: "Date",
        field: "conversionDate",
        menuTabs: ["filterMenuTab"],
        editable: isEditable,
        cellRenderer: "dateRender",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: string) => {
            if (!cellValue) {
              return -1;
            }
            const cellDate = new Date(cellValue);
            const filterDateOnly = new Date(
              dateFromFilter.getFullYear(),
              dateFromFilter.getMonth(),
              dateFromFilter.getDate()
            );
            const cellDateOnly = new Date(
              cellDate.getFullYear(),
              cellDate.getMonth(),
              cellDate.getDate()
            );

            if (cellDateOnly < filterDateOnly) {
              return -1;
            } else if (cellDateOnly > filterDateOnly) {
              return 1;
            }
            return 0;
          },
          browserDatePicker: true,
        },
        valueGetter: (params) => params.data?.conversionDate || null,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return new Date(params.value).toLocaleDateString();
        },
        getQuickFilterText: (params) => {
          if (!params.data?.conversionDate) return "";
          return new Date(params.data.conversionDate).toLocaleDateString();
        },
        tooltipValueGetter: (params) => {
          if (!params.data?.conversionDate) return "";
          return new Date(params.data.conversionDate).toLocaleDateString();
        },
      },
      {
        headerName: "Final Security Name",
        field: "conversionSecurityId",
        cellRenderer: "securityTypeRender",
        menuTabs: ["filterMenuTab"],
        editable: isEditable,
        valueGetter: (params) => {
          const instrument = instrumentClassLookupMap.get(
            params.data?.conversionSecurityId || ""
          );
          return instrument?.name || params.data?.conversionSecurityId || "";
        },
        getQuickFilterText: (params) => {
          const instrument = instrumentClassLookupMap.get(
            params.data?.conversionSecurityId || ""
          );
          return instrument?.name || params.data?.conversionSecurityId || "";
        },
        tooltipField: "tooltipFinalSecurity",
      },
      {
        headerName: "Number of shares after conversion",
        field: "noOfSharesAfterConversion",
        menuTabs: ["filterMenuTab"],
        valueFormatter: (params) =>
          params.value ? params.value.toLocaleString() : "0",
        tooltipField: "tooltipConvertedShares",
      },
    ],
    [shareholderLookupMap, instrumentClassLookupMap, isEditable]
  );
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  const onCellValueChanged = useCallback(
    (event: any) => {
      const updatedData = [...rowData];
      const rowIndex = event.rowIndex;
      updatedData[rowIndex] = event.data;
      setRowData(updatedData);
      onDataChange?.(updatedData);
    },
    [rowData, onDataChange]
  );

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  function setTableHeight() {
    const displayedRowCount = gridApi.current?.api?.getDisplayedRowCount() || 0;
    if (displayedRowCount === 0) return 200;
    if (displayedRowCount === 1) return (displayedRowCount + 2.78) * 60;
    if (displayedRowCount === 2) return (displayedRowCount + 2.94) * 60;
    if (displayedRowCount === 3) return (displayedRowCount + 3) * 60;
    if (displayedRowCount === 4) return (displayedRowCount + 2.75) * 60;
    if (displayedRowCount === 5) return (displayedRowCount + 2.7) * 60;
    if (displayedRowCount === 6) return (displayedRowCount + 2.55) * 60;
    if (displayedRowCount === 7) return (displayedRowCount + 2.35) * 60;
    if (displayedRowCount === 8) return (displayedRowCount + 2.28) * 60;
    if (displayedRowCount === 9) return (displayedRowCount + 2.22) * 60;
    return 10 * 60;
  }

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== "action-column") {
      const template = cellParams.data;
      if (
        cellParams.value.name !== "Total" &&
        cellParams.column.getColId() === "investorName"
      ) {
        onClickShareholderName(template.investorId);
      }
    }
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    if (
      !data.childrenAfterAggFilter ||
      data.childrenAfterAggFilter.length === 0
    ) {
      api.setPinnedBottomRowData([]);
      return;
    }

    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );

    const totalSourceShares = filteredData.reduce(
      (acc: number, row: any) => acc + (Number(row.sourceNoOfShares) || 0),
      0
    );

    const totalConvertedShares = filteredData.reduce(
      (acc: number, row: any) =>
        acc + (Number(row.noOfSharesAfterConversion) || 0),
      0
    );

    api.setPinnedBottomRowData([
      {
        investorName: { name: "Total", type: null },
        sourceNoOfShares: totalSourceShares,
        noOfSharesAfterConversion: totalConvertedShares,
        tooltipSourceShares: totalSourceShares.toLocaleString(),
        tooltipConvertedShares: totalConvertedShares.toLocaleString(),
        currencyType,
        currencySymbol,
      },
    ]);
  }

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);
    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    setPinnedBottomRowData(grid);
  };

  return (
    <div className="border border-borderColor bg-white shadow-box">
      <div className="flex flex-col lg:flex-row justify-start lg:justify-between py-2 lg:items-center items-start bg-white rounded-md">
        <GenericTableHeader
          heading={"Conversion Table Data"}
          subHeading={"Shareholders"}
          count={
            isFilterApplied === true ? filteredRowData.length : rowData.length
          }
        />
        <div className="justify-end py-2 items-center gap-4 px-4 flex flex-col sm:flex-row">
          <div className="w-full sm:w-auto">
            <SearchInput
              placeholder="Search"
              onChange={(e: any) => {
                gridApi.current?.api?.setQuickFilter(e.target.value);
              }}
            />
          </div>

          <IconCTAButton
            value={"Columns"}
            onClick={() => openToolPanel("columns")}
            iconName={"fluent:column-triple-edit-20-regular"}
            className={`px-4 font-medium items-center flex flex-row ${
              isColumnOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
          <IconCTAButton
            value={"Filters"}
            onClick={() => openToolPanel("filters")}
            iconName={"material-symbols:filter-alt"}
            className={`px-4 font-medium items-center flex flex-row ${
              isFilterOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
        </div>
      </div>

      <div className="justify-between w-full flex">
        <div
          style={{
            height: setTableHeight(),
          }}
          className="w-full max-h-full overflow-x-auto ag-theme-material h-full"
        >
          <AgGridReact<ConversionDto>
            ref={gridApi}
            sideBar={sideBar}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            components={components}
            onGridReady={(params) => {
              gridApi.current = params;
            }}
            onCellValueChanged={onCellValueChanged}
            onFilterChanged={onAgGridFilterChanged}
            onRowDataUpdated={setPinnedBottomRowData}
            alwaysShowHorizontalScroll
            onCellClicked={handleCellClick}
            alwaysMultiSort
            animateRows={true}
            pagination={false}
            enableCharts={true}
            enableRangeSelection={true}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass="border-t border-dashed"
            gridOptions={gridOptions}
            overlayNoRowsTemplate='<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Conversion Data To Show</span>'
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ConversionTableGrid;
